import {
  Box,
  Divider,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, CloseIcon } from "@chakra-ui/icons";
import { MdOutlineEmail } from "react-icons/md";
import { Controller, useFormContext } from "react-hook-form";
import {
  useInstantSearch,
  useSearchBox,
  useInfiniteHits,
} from "react-instantsearch";
import _ from "lodash";

import KYDButton from "src/ui/KYDButton";
import KYDHighlightTextArea from "../../../ui/KYDHighlightTextArea";
import KYDInput from "src/ui/KYDInput";
import KYDCheckbox from "src/ui/KYDCheckbox";
import KYDSearchInput from "src/ui/KYDSearchInput";

import { ReschedulingCampaignSchema, ReschedulingFormValues } from "./schema";
import { AlgoliaAttendeeObject } from "@common/interfaces/Algolia";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { EventDetailTimelinesSectionData } from "@common/interfaces/KYDEvent";
import { formatDateForCampaign } from "src/utils";

const Header = ({ title, info }: { title: string; info?: string }) => {
  return (
    <HStack width={"full"} align="center" spacing={4}>
      <Text
        color="#1A202C"
        fontSize="24px"
        fontWeight="600"
        letterSpacing="-0.72px"
      >
        {title}
      </Text>
      {info && (
        <Text color="#1A202C80" fontWeight="400" fontSize="16px">
          {info}
        </Text>
      )}
    </HStack>
  );
};

const Description = ({
  description,
  variant = "default",
}: {
  description: string;
  variant?: "default" | "highlight";
}) => {
  return (
    <Text
      width={"full"}
      fontFamily={"Helvetica"}
      color={variant === "default" ? "#666666" : "#000000CC"}
      fontWeight="400"
      fontSize={variant === "default" ? "12px" : "14px"}
    >
      {description}
    </Text>
  );
};

const ReschedulingCampaignModalCampaign = ({
  data,
  organizationUsers,
  onCloseModal,
  onTestEmail,
  updatedStartAt,
  loading,
  setShowPreview,
  showPreview,
  isMobile,
  user,
}: {
  data: EventDetailTimelinesSectionData;
  organizationUsers: KYDOrganizationUser[];
  onCloseModal: () => void;
  onTestEmail: () => void;
  updatedStartAt: string;
  loading: boolean;
  setShowPreview: (showPreview: boolean) => void;
  showPreview: boolean;
  isMobile: boolean;
  user: KYDOrganizationUser;
}) => {
  const { items: attendees } = useInfiniteHits<AlgoliaAttendeeObject>({
    escapeHTML: false,
    showPrevious: false,
  });

  const form = useFormContext<ReschedulingFormValues>();
  const testEmails = form.watch("test_email_recipients") || [];
  const { status } = useInstantSearch();
  const { refine } = useSearchBox();

  const canSendTestEmail =
    testEmails.length > 0 && form.watch("simulated_attendee_id") && !loading;

  const isMobilePreviewOn = showPreview && isMobile;

  const substitutionsItems = [
    {
      key: "{{previous_start_at}}",
      label: "Old Date",
      value: formatDateForCampaign(data.start_at, data.timezone),
    },
    {
      key: "{{updated_start_at}}",
      label: "New Date",
      value: formatDateForCampaign(updatedStartAt, data.timezone),
    },
    {
      key: "{{event_name}}",
      label: "Event Name",
      value: data.event_name,
    },
    {
      key: "{{venue}}",
      label: "Venue",
      value: data.venue.name,
    },
    {
      key: "{{first_name}}",
      label: "First Name",
      value: user?.first_name,
    },
    {
      key: "{{last_name}}",
      label: "Last Name",
      value: user?.last_name,
    },
  ];

  const onAddTestEmail = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = (e.target as HTMLInputElement).value.trim();

      if (!value) return;

      const current = form.getValues("test_email_recipients") || [];

      const emails = value
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email);

      const uniqueEmails = Array.from(new Set([...current, ...emails]));

      try {
        await ReschedulingCampaignSchema.validateAt("test_email_recipients", {
          test_email_recipients: uniqueEmails,
        });

        form.setValue("test_email_recipients", uniqueEmails);
        form.clearErrors("test_email_recipients");
        (e.target as HTMLInputElement).value = "";
      } catch (err: any) {
        form.setError("test_email_recipients", {
          type: "manual",
          message: err.message,
        });
      }
    }
  };

  const onRemoveTestEmail = (email: string) => {
    const current = form.getValues("test_email_recipients") || [];
    const next = current.filter((e) => e !== email);
    form.setValue("test_email_recipients", next);
  };

  const onChangeTestEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    const current = form.getValues("test_email_recipients") || [];
    const next = current.includes(email)
      ? current.filter((e) => e !== email)
      : [...current, email];

    const uniqueEmails = Array.from(new Set(next));
    form.setValue("test_email_recipients", uniqueEmails);
  };

  const onSelectUser = (user: KYDOrganizationUser) => {
    form.setValue("simulated_attendee_id" as any, user.id);
  };

  return (
    <Box w={"100%"} h={"100vh"} py={6}>
      <HStack
        position={isMobile ? "fixed" : "static"}
        top={0}
        py={isMobile ? 4 : 0}
        px={4}
        zIndex={10}
        bg="white"
        width="100%"
        justifyContent={isMobilePreviewOn ? "center" : "space-between"}
      >
        {!isMobilePreviewOn && (
          <KYDButton
            borderRadius="6px"
            variant="outline"
            onClick={onCloseModal}
            mb={4}
            width={isMobile ? "80px" : "fit-content"}
            paddingX={4}
            leftIcon={
              <Icon
                as={ChevronLeftIcon}
                fontSize={20}
                mr={-1}
                color="#00000069"
              />
            }
            fontSize={isMobile ? "12px" : "14px"}
            boxShadow="0px 10px 15px rgba(0, 0, 0, 0.05)"
          >
            Back
          </KYDButton>
        )}
        {isMobile && (
          <KYDButton
            isDisabled={loading}
            borderRadius="6px"
            mb={4}
            width={isMobile ? "80px" : "fit-content"}
            backgroundColor="black"
            onClick={() => setShowPreview(!showPreview)}
            paddingX={4}
            fontSize={isMobile ? "12px" : "14px"}
            boxShadow="0px 10px 15px rgba(0, 0, 0, 0.05)"
          >
            {isMobilePreviewOn ? <Icon as={CloseIcon} /> : "Preview"}
          </KYDButton>
        )}
        {!isMobilePreviewOn && (
          <KYDButton
            isDisabled={loading}
            borderRadius="6px"
            type="submit"
            mb={4}
            width={isMobile ? "80px" : "fit-content"}
            paddingX={4}
            leftIcon={<Icon as={MdOutlineEmail} />}
            fontSize={isMobile ? "12px" : "14px"}
            boxShadow="0px 10px 15px rgba(0, 0, 0, 0.05)"
          >
            Send
          </KYDButton>
        )}
      </HStack>
      <Box px={4} pr={4} mt={isMobile ? 14 : 0} w="100%">
        <Text width={"full"} fontWeight="400" fontSize="24px" mb={4}>
          Rescheduling Campaign
        </Text>
        <VStack
          direction={{ base: "column", md: "row" }}
          w="100%"
          alignItems={"flex-start"}
          spacing={{ base: 26 }}
        >
          <Stack width={"full"}>
            <Header title="Subject" />
            <Description description="Highlighted fields are prefilled with dynamic data" />
            <Controller
              name="subject"
              control={form.control}
              render={({ field }) => (
                <KYDHighlightTextArea
                  {...field}
                  placeholder="Enter subject line"
                  regex={/\{\{([^}]+)\}\}/g}
                  height="80px"
                  width="100%"
                  substitutions={substitutionsItems}
                  removePlaceholderOnSingleKey
                  onChangePreview={(value) => {
                    form.setValue("subject_preview", value);
                  }}
                />
              )}
            />
            <Controller
              name="preheader"
              control={form.control}
              render={({ field }) => (
                <KYDInput
                  {...field}
                  placeholder="Preheader text"
                  error={form.formState.errors.preheader?.message}
                />
              )}
            />
          </Stack>
          <Divider
            opacity={1}
            borderColor="#00000026"
            mx={-4}
            width={"calc(100% + 32px)"}
          />
          <Stack width={"full"}>
            <Header title="Message" />
            <Description description="Highlighted fields are prefilled with dynamic data" />
            <Controller
              name="message_from_venue"
              control={form.control}
              render={({ field }) => (
                <KYDHighlightTextArea
                  {...field}
                  placeholder="Enter message"
                  regex={/\{\{([^}]+)\}\}/g}
                  height="180px"
                  width="100%"
                  substitutions={substitutionsItems}
                  removePlaceholderOnSingleKey
                  onChangePreview={(value) => {
                    form.setValue("message_from_venue_preview", value);
                  }}
                />
              )}
            />
            <Controller
              name="additional_message"
              control={form.control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder="Additional message (optional)"
                  border="1px solid #E2E8F0"
                  mt={1}
                  borderRadius="8px"
                  fontSize="14px"
                />
              )}
            />
          </Stack>
          <Divider
            opacity={1}
            borderColor="#00000026"
            mx={-4}
            width={"calc(100% + 32px)"}
          />
          <Stack width={"full"}>
            <Header title="Test Email" />

            <VStack width={"full"} spacing={2} align="flex-start">
              {loading ? (
                <Spinner />
              ) : (
                organizationUsers.map((user) => (
                  <HStack key={user.id} spacing={2}>
                    <KYDCheckbox
                      key={user.id}
                      value={user.email}
                      onChange={onChangeTestEmail}
                      isChecked={testEmails.includes(user.email)}
                    />
                    <Text
                      fontSize="14px"
                      color="#00000099"
                      fontFamily="Helvetica"
                      as="label"
                      htmlFor={`email-${user.email}`}
                    >
                      {user.email}
                    </Text>
                  </HStack>
                ))
              )}
            </VStack>
            <KYDInput
              onKeyDown={onAddTestEmail}
              placeholder="Add email addresses"
              error={form.formState.errors.test_email_recipients?.message}
            />
            <Description
              description={
                "You can add up to 10 emails, separating each with a comma"
              }
            />
            {testEmails.length > 0 && (
              <Box border="1px solid #E2E8F0" borderRadius="8px" p={3} mb={3}>
                {testEmails?.map((email) => (
                  <HStack key={email} justifyContent="space-between">
                    <Text fontSize="12px">{email}</Text>
                    <IconButton
                      aria-label="Remove email"
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => onRemoveTestEmail(email)}
                    />
                  </HStack>
                ))}
              </Box>
            )}
            <Text
              fontWeight="600"
              fontSize="12px"
              color="#00000099"
              fontFamily="Helvetica"
            >
              Simulate ticket holder
            </Text>
            <KYDSearchInput
              items={attendees.filter((a) => !!a.email)}
              onSelect={onSelectUser}
              onChange={(value) => {
                refine(value);
              }}
              placeholder="Search for a subscriber"
              searchField="email"
              displayField="email"
              loading={status === "loading"}
            />
            <Description
              description={
                "This will send the test email as if it's sent to the chosen subscriber. " +
                "Great for testing personalization or referral programs."
              }
            />

            <KYDButton
              backgroundColor="#2193F3"
              borderRadius="full"
              isDisabled={!canSendTestEmail}
              onClick={onTestEmail}
              height={"44px"}
              mb={4}
              mt={2}
              isLoading={loading}
              width={"fit-content"}
            >
              Send test email
            </KYDButton>
          </Stack>
        </VStack>
      </Box>
    </Box>
  );
};

export default ReschedulingCampaignModalCampaign;
